import React, { useCallback } from "react";
import { Maybe } from "true-myth";
import styled from "styles/styled_components";

import { IFormValues } from "components/schedule/time_block_form";
import Button, { ButtonColor } from "components/shared/button";

import { dateToWeekday } from "helpers/dates";
import { ExistingBlock } from "helpers/schedule/time_block_modal";
import {
  IPosUser,
  IShift,
  IUnavailabilityBlock,
  IPerson,
  Frequency,
} from "types/schedules";

const StyledActionButtons = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-around;
`;

const StyledNotice = styled.div`
  margin-bottom: ${props => props.theme.spacing.medium};
  min-width: 100%;
  padding: 0;
  text-align: center;
`;

interface ITimeBlockButtons {
  existingBlock: ExistingBlock;
  disableSave: boolean;
  formValues: IFormValues;
  isExistingUnavail: boolean;
  isConfirmingDelete: boolean;
  isUnavail: boolean;
  person: IPerson;
  deleteShift: (shift: IShift, assignee: Maybe<IPosUser>) => void;
  saveUnavailException: (exception: IUnavailabilityBlock) => void;
  setConfirmingDelete: (newVal: boolean) => void;
  deleteUnavail: (unavail: IUnavailabilityBlock) => void;
  onCancel: () => void;
}

export default function TimeBlockButtons({
  deleteShift,
  deleteUnavail,
  disableSave,
  existingBlock,
  formValues,
  isExistingUnavail,
  isConfirmingDelete,
  isUnavail,
  person,
  saveUnavailException,
  setConfirmingDelete,
  onCancel,
}: ITimeBlockButtons) {
  // Form Action DELETE
  const onDelete = useCallback(
    () => {
      if (isExistingUnavail) {
        existingBlock.map(block => {
          const unavail = block as IUnavailabilityBlock;
          if (unavail.frequency === Frequency.WEEKLY && !isConfirmingDelete) {
            // When deleting recurring unavails, go through an additional confirmation step.
            setConfirmingDelete(true);
          } else {
            deleteUnavail(unavail);
            onCancel();
          }
          return unavail;
        });
      } else {
        existingBlock.map(shift => {
          deleteShift(shift as IShift, person.assignee);
          onCancel();
          return shift;
        });
      }
    },
    [
      deleteShift,
      existingBlock,
      formValues,
      isConfirmingDelete,
      onCancel,
      person,
    ]
  );

  const createSingleRecurrenceException = useCallback(
    () => {
      existingBlock.map(block => {
        const unavail = block as IUnavailabilityBlock;
        const exception: IUnavailabilityBlock = {
          ...unavail,
          uuid: "",
          date: formValues.date.meta,
          frequency: Frequency.ONCE,
          parentId: unavail.uuid,
          type: "EXCEPTION",
        };
        saveUnavailException(exception);
        onCancel();
        return unavail;
      });
    },
    [existingBlock, saveUnavailException]
  );

  const deleteButtons: JSX.Element[] = existingBlock.match({
    Just: () => {
      if (isConfirmingDelete) {
        return [
          <Button
            key="delete-this"
            label="This instance"
            buttonColor={ButtonColor.BLUE}
            onClick={createSingleRecurrenceException}
          />,
          <Button
            key="delete-all"
            label="All instances"
            buttonColor={ButtonColor.BLUE}
            onClick={onDelete}
          />,
        ];
      } else {
        return [
          <Button
            key="delete"
            label={`Delete${isUnavail ? "" : " Shift"}`}
            buttonColor={ButtonColor.RED}
            onClick={onDelete}
          />,
        ];
      }
    },
    Nothing: () => [],
  });

  const employeeName = person.assignee
    .map(thisAssignee => thisAssignee.firstName)
    .unwrapOr("Employee");
  const dayOfWeek = existingBlock
    .map(thisBlock => dateToWeekday(thisBlock.startTime) as string)
    .unwrapOr("week");

  return (
    <StyledActionButtons>
      {isConfirmingDelete && (
        <StyledNotice>
          {employeeName} is unavailable every {dayOfWeek}.
        </StyledNotice>
      )}
      {deleteButtons}
      {!isConfirmingDelete && (
        <Button
          label="Save"
          buttonColor={ButtonColor.BLUE}
          disabled={disableSave}
          type="submit"
        />
      )}
    </StyledActionButtons>
  );
}
